import React, { Dispatch, SetStateAction, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { darkenColor, formatDate } from "../../exports/functions";
import { IUserDetails } from "../../exports/interfaces";
import { Eye, Pencil } from "lucide-react";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { usePermission } from "../../hooks/usePermission";
import { useOption } from "../../context/OptionContext";

export const getTRoleClass = (type: string) => {
  switch (type) {
    case "Complaint Officer":
      return "pill-primary";
    case "Registration Intern":
      return "pill-danger";
    case "Notice Admin":
      return "bg-warning text-dark";
    case "Registeration Officer":
      return "pill-info";
    case "Super Admin":
      return "pill-success";
    default:
      return "bg-secondary";
  }
};

export const getStatusClass = (status: string) => {
  switch (status) {
    case "Active":
      return "pill-success";
    case "Inactive":
      return "pill-danger";
    case "Deactivated":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

const UserTable = ({
  user,
  setUser,
}: {
  user: IUserDetails[];
  setUser: Dispatch<SetStateAction<IUserDetails[]>>;
}) => {
  const { setLoading } = useLoader();
  const { statusOptions } = useOption();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = user.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(user.length / itemsPerPage);

  const handleClose = () => {
    setShowDelete(false);
  };
  const handleDelete = async () => {
    setLoading(true);

    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/users/${deleteId}`);

      if (response.status === 200 || response.status === 201) {
        setUser((prev) => prev.filter((item) => item.id !== deleteId));

        setShowDelete(false);
        setDeleteId(null);

        setTimeout(() => {
          setLoading(false);
          dynamicSuccess("User deleted successfully!");
        }, 1000);
      }
    } catch (error: any) {
      setTimeout(() => {
        setLoading(false);
        dynamicFailed(`${error.message}`);
      }, 1000);
    }
  };

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "User Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };
  return (
    <>
      <div className="dashboard-card">
        <div className="card-body">
          <h6 className="mb-3">USER MANAGEMENT</h6>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>S. no.</th>
                  <th>User Name</th>
                  <th>Email Address</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th className="sticky-action">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index}>
                    <td className="text-danger">
                      {String(indexOfFirstItem + index + 1).padStart(6, "0")}
                    </td>

                    <td>{item.userName}</td>

                    <td>{item.email}</td>

                    <td>
                      {" "}
                      <span
                        className={`badge rounded-pill px-3 py-2 ${getTRoleClass(item.userRole)}`}
                      >
                        {item.userRole}
                      </span>
                    </td>

                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2`}
                         style={statusStyle(item.userStatus)}
                      >
                        {item.userStatus}
                      </span>
                    </td>

                    <td>
                      {item.lastLogin
                        ? formatDate(new Date(item.lastLogin))
                        : "-"}
                    </td>

                    <td className="sticky-action">
                      <div className="d-flex gap-3 align-items-center">
                        <Link to={`/user/view/${item.id}`}>
                          <i
                            className="cursor-pointer"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="View"
                          >
                            <Eye />
                          </i>
                        </Link>
                        <Link
                          to={
                            canEdit("Users")
                              ? `/user/edit/${item.id}`
                              : "#"
                          }
                          onClick={(e) => {
                            if (!canEdit("Users")) {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            pointerEvents: canEdit("Users") ? "auto" : "none",
                            opacity: canEdit("Users") ? 1 : 0.5,
                          }}
                        >
                          <i
                            data-toggle="tooltip"
                            data-placement="top"
                            title={canEdit("Users") ? "Edit" : "No permission"}
                          >
                            <Pencil size={20} />
                          </i>
                        </Link>
                        <i
                          className={`fa-solid fa-trash text-danger ${
                            canDelete("Users") ? "cursor-pointer" : "opacity-50"
                          }`}
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            canDelete("Users") ? "Delete" : "No permission"
                          }
                          style={{
                            pointerEvents: canDelete("Users") ? "auto" : "none",
                          }}
                          onClick={() => {
                            if (!canDelete("Users")) return;

                            setDeleteId(item.id || 0);
                            setShowDelete(true);
                          }}
                        ></i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`d-flex justify-content-between align-items-center flex-wrap ${isMobile ? "gap-2" : ""}`}
        >
          <small>
            {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, user.length)} of{" "}
            {user.length} Users
          </small>

          <div className="pagination">
            <button
              className="pagination-arrow"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              <i className="fa-solid fa-angle-left"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-page ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination-arrow"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              <i className="fa-solid fa-angle-right"></i>
            </button>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default UserTable;
